import {
  observable, action, set, computed,
} from 'mobx';
import Routes, { ExternalRoute } from '../../../application/routes/Routes';
import { diContainer, serviceNames } from '../../../diContainer/diContainer';
import SessionRepository from '../../../repositories/SessionRepository';
import DateUtils from '../../../utils/DateUtils';

export const CHECK_SESSION_TIMEOUT = 5000;

export interface ISession {
  sessionRepository: SessionRepository;
  timeout: number;
  sessionIdle: number;
}

export default class Session {
  @observable sessionRepository: SessionRepository;

  @observable routes: Routes;

  @observable timeout: number;

  @observable sessionIdle: number;

  @observable isReady = false;

  constructor() {
    this.sessionRepository = diContainer.get(serviceNames.sessionRepository);
    this.routes = diContainer.get(serviceNames.routes);
  }

  static fromJSON(data: ISession): Session {
    const session = new Session();
    set(session, data);

    return session;
  }

  @action
  updateSessionIdle = (value: number): void => {
    this.sessionIdle = value;
  };

  /*
  * store session expiration timestamp
  */
  setSession = (): void => {
    const now = Date.now();
    const sessionExpiration = now + this.sessionIdle;
    this.sessionRepository.setSessionIdle(sessionExpiration);
    this.setSessionTimeout();
  };

  resetSession = (): void => {
    this.deleteSessionIfExpired();
    clearTimeout(this.timeout);
    this.setSession();
  };

  eventListenerHandler = (): void => {
    this.deleteSessionIfExpired();
  };

  /*
  * set timeout that verify if session should end
  * if still avaiable set another timeout to verify again
  * when the session is expected to expiry or in CHECK_SESSION_TIMEOUT
  */
  setSessionTimeout = (timeout?: number): void => {
    this.timeout = window.setTimeout(this.setTimeoutHandler, timeout || CHECK_SESSION_TIMEOUT);
  };

  setTimeoutHandler = (): void => {
    this.deleteSessionIfExpired();
    const lastTimeout = this.sessionRepository.getSessionIdle();
    const nextTimeout = Math.min(lastTimeout - Date.now(), CHECK_SESSION_TIMEOUT);
    this.setSessionTimeout(nextTimeout);
  };

  deleteSessionIfExpired = (): void => {
    if (!this.isSessionValid) {
      this.sessionRepository.deleteSessionIdle();
      this.routes.redirect(ExternalRoute.SESSION_EXPIRED, 'reason=client_idle');
    }
  };

  @action
  init(minutesTimeout: number): void {
    const sessionIdle = DateUtils.convertMinutesToMiliseconds(minutesTimeout);
    this.updateSessionIdle(sessionIdle);
    this.setSession();
    this.registerUIListeners();
    this.isReady = true;
  }

  private registerUIListeners(): void {
    window.addEventListener('click', this.eventListenerHandler);
    window.addEventListener('scroll', this.eventListenerHandler);
  }

  @computed
  get isSessionValid(): boolean {
    const lastTimeout = this.sessionRepository.getSessionIdle();

    return !this.isReady || !lastTimeout || lastTimeout > Date.now();
  }
}
